import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, FileText, Shield, AlertTriangle, Users, CreditCard, Database, Mail } from 'lucide-react';
import './Terms.css';

export default function Terms() {
  return (
    <div className="legal-page">
      <div className="legal-container">
        {/* Header */}
        <div className="legal-header">
          <Link to="/login" className="back-button">
            <ArrowLeft size={20} />
            Volver
          </Link>
          <div className="legal-title-section">
            <div className="legal-icon">
              <FileText size={32} />
            </div>
            <div>
              <h1 className="legal-title">Términos y Condiciones</h1>
              <p className="legal-subtitle">Última actualización: {new Date().toLocaleDateString('es-ES')}</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="legal-content">
          <section className="legal-section">
            <h2 className="section-title">
              <Users size={24} />
              1. Aceptación de los Términos
            </h2>
            <p className="section-text">
              Al acceder y utilizar Monitoreo FIFA Collect ("la Plataforma"), usted acepta estar sujeto a estos Términos y Condiciones. 
              Si no está de acuerdo con alguna parte de estos términos, no debe utilizar nuestros servicios.
            </p>
            <p className="section-text">
              Estos términos constituyen un acuerdo legal entre usted y Monitoreo FIFA Collect. Al crear una cuenta o utilizar nuestros servicios, 
              confirma que ha leído, entendido y acepta estos términos en su totalidad.
            </p>
          </section>

          <section className="legal-section">
            <h2 className="section-title">
              <Shield size={24} />
              2. Descripción del Servicio
            </h2>
            <p className="section-text">
              Monitoreo FIFA Collect es una plataforma de monitoreo de precios que permite a los usuarios:
            </p>
            <ul className="legal-list">
              <li>Monitorear precios de coleccionables FIFA en tiempo real</li>
              <li>Recibir alertas por correo electrónico cuando los precios alcancen umbrales específicos</li>
              <li>Acceder a estadísticas y análisis de mercado</li>
              <li>Gestionar múltiples partidos y configuraciones de monitoreo</li>
            </ul>
            <p className="section-text">
              <strong>Importante:</strong> Nuestra plataforma no está afiliada con FIFA, EA Sports, Electronic Arts o cualquier entidad oficial de FIFA.
            </p>
          </section>

          <section className="legal-section">
            <h2 className="section-title">
              <CreditCard size={24} />
              3. Suscripciones y Pagos
            </h2>
            <h3 className="subsection-title">3.1 Planes de Suscripción</h3>
            <p className="section-text">
              Ofrecemos diferentes planes de suscripción con características y límites específicos:
            </p>
            <ul className="legal-list">
              <li><strong>Plan Básico:</strong> Monitoreo limitado con alertas básicas</li>
              <li><strong>Plan Premium:</strong> Monitoreo extendido con más partidos y frecuencia</li>
              <li><strong>Plan Élite:</strong> Acceso completo a todas las funcionalidades</li>
            </ul>

            <h3 className="subsection-title">3.2 Facturación y Renovación</h3>
            <p className="section-text">
              Las suscripciones se renuevan automáticamente al final de cada período de facturación. 
              Los pagos se procesan a través de Stripe, nuestro proveedor de pagos seguro.
            </p>

            <h3 className="subsection-title">3.3 Cancelación</h3>
            <p className="section-text">
              Puede cancelar su suscripción en cualquier momento desde su panel de usuario. 
              La cancelación será efectiva al final del período de facturación actual.
            </p>
          </section>

          <section className="legal-section">
            <h2 className="section-title">
              <Database size={24} />
              4. Uso de Datos y Privacidad
            </h2>
            <p className="section-text">
              Recopilamos y procesamos datos de precios de fuentes públicas para proporcionar nuestros servicios de monitoreo. 
              Su información personal se maneja de acuerdo con nuestra Política de Privacidad.
            </p>
            <p className="section-text">
              <strong>Datos que recopilamos:</strong>
            </p>
            <ul className="legal-list">
              <li>Información de cuenta (email, nombre de usuario)</li>
              <li>Configuraciones de monitoreo y alertas</li>
              <li>Datos de uso de la plataforma</li>
              <li>Información de pago (procesada por Stripe)</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2 className="section-title">
              <AlertTriangle size={24} />
              5. Limitaciones y Descargo de Responsabilidad
            </h2>
            <h3 className="subsection-title">5.1 Precisión de Datos</h3>
            <p className="section-text">
              Aunque nos esforzamos por proporcionar datos precisos y actualizados, no garantizamos la exactitud, 
              completitud o actualidad de toda la información de precios. Los precios pueden variar y estar sujetos a cambios.
            </p>

            <h3 className="subsection-title">5.2 Decisiones de Inversión</h3>
            <p className="section-text">
              <strong>IMPORTANTE:</strong> Nuestra plataforma es únicamente para fines informativos. 
              No proporcionamos asesoramiento financiero o de inversión. Todas las decisiones de compra y venta 
              son responsabilidad exclusiva del usuario.
            </p>

            <h3 className="subsection-title">5.3 Disponibilidad del Servicio</h3>
            <p className="section-text">
              No garantizamos la disponibilidad continua del servicio. Podemos experimentar interrupciones por mantenimiento, 
              actualizaciones o problemas técnicos.
            </p>
          </section>

          <section className="legal-section">
            <h2 className="section-title">
              <Users size={24} />
              6. Responsabilidades del Usuario
            </h2>
            <p className="section-text">Como usuario de nuestra plataforma, usted se compromete a:</p>
            <ul className="legal-list">
              <li>Proporcionar información precisa y actualizada</li>
              <li>Mantener la confidencialidad de su cuenta</li>
              <li>No utilizar el servicio para actividades ilegales</li>
              <li>Respetar los límites de uso según su plan de suscripción</li>
              <li>No intentar acceder a sistemas o datos no autorizados</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2 className="section-title">
              <Shield size={24} />
              7. Modificaciones y Terminación
            </h2>
            <p className="section-text">
              Nos reservamos el derecho de modificar estos términos en cualquier momento. 
              Los cambios significativos serán notificados a través de la plataforma o por correo electrónico.
            </p>
            <p className="section-text">
              Podemos suspender o terminar su cuenta si viola estos términos o por razones de seguridad.
            </p>
          </section>

          <section className="legal-section">
            <h2 className="section-title">
              <Mail size={24} />
              8. Contacto
            </h2>
            <p className="section-text">
              Si tiene preguntas sobre estos Términos y Condiciones, puede contactarnos:
            </p>
            <div className="contact-info">
              <p><strong>Email:</strong> blueecodelabs@gmail.com</p>
            </div>
          </section>
        </div>

        {/* Footer Actions */}
        <div className="legal-footer">
          <Link to="/privacy" className="legal-link">
            <Shield size={16} />
            Política de Privacidad
          </Link>
          <Link to="/support" className="legal-link">
            <Mail size={16} />
            Soporte
          </Link>
        </div>
      </div>
    </div>
  );
}